import React, { useMemo } from 'react'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  arePropertyRatesLoading,
  getCheckoutDate,
  getPropertyMappedBedbankOffer,
  getPropertyPackageOptionsByRoom,
} from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PropertyRoomOptionMobile from 'components/OfferPage/Property/MobileBookingFlow/PropertyRoomOptionMobile'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { RoomPackageOptions } from 'components/OfferPage/Property/singlePropertyUtils'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOnMobileStickyHeaderFilters'

interface Props {
  room: App.PackageRoomType;
  selectedBedbankRoomId?: string;
  offer: App.HotelOffer;
  bedbankOffer?: App.BedbankOffer;
  packageOptionsByRoom: Array<RoomPackageOptions>;
  airports?: Array<App.Airport>;
  isLoading: boolean;
  onClose?: () => void;
  onBack?: () => void;
}

function SinglePropertyPackageSelectionModal(props: Props) {
  const {
    room,
    selectedBedbankRoomId,
    offer,
    bedbankOffer,
    packageOptionsByRoom,
    airports,
    isLoading,
    onClose,
    onBack,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const selectedRoom = useMemo(() => packageOptionsByRoom.find(option => option.bedbank.some(bedbank => bedbank.package.roomId === selectedBedbankRoomId) || option.lpc.some(lpc => lpc.package.roomType?.id === room?.id)), [packageOptionsByRoom, room?.id, selectedBedbankRoomId])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [packageOptionsByRoom]) // regenerate id if list of rooms changes

  return (
    <>
      <ModalHeader
        title="Select a package"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AlwaysOnMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        {selectedRoom && <PropertyRoomOptionMobile
          key={selectedRoom.roomId}
          offer={offer}
          packageViews={selectedRoom.lpc}
          bedbankViews={selectedRoom.bedbank}
          bedbankOffer={bedbankOffer}
          flightDestination={destinationAirport}
          roomListId={roomListId}
        />}
        {isLoading && <LoadingIndicator inline />}
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  selectedBedbankRoomId: state.selectedBedbankRoomId,
  offer: state.offer,
  bedbankOffer: getPropertyMappedBedbankOffer(state, appState),
  packageOptionsByRoom: getPropertyPackageOptionsByRoom(state, appState),
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  duration: state.duration,
  airports: appState.geo.airports,
  isLoading: arePropertyRatesLoading(state, appState),
}), SinglePropertyPackageSelectionModal)
